<template>
  <PieChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    show-interval
    class="make-a-copy"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import PieChart from '@/components/cards/models/PieChart';
import { mapActions } from 'vuex';
import EventBus from '@/utils/event-bus';
import colors from '@/utils/colors';
import { SAC_STATUS } from '@/utils/constants';

export default {
  name: 'AverageSACTimePieChart',
  components: {
    PieChart,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
    };
  },
  computed: {
    legendLabels() {
      return [
        this.$t('components.AverageSACTimePieChart.label.waiting'),
        this.$t('components.AverageSACTimePieChart.label.open'),
        this.$t('components.AverageSACTimePieChart.label.answer'),
        this.$t('components.AverageSACTimePieChart.label.finished'),
      ];
    },
    default_time() {
      return this.$t('components.AverageSACTimePieChart.date.default');
    },
    series() {
      return this.extractAverageTimeData();
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getPerformanceService',
    ]),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      this.getPerformanceService({
        itemId: this.itemId,
        servicePath: 'reportAvgTime',
        serviceParams: {
          filter: this.filter,
        },
      })
        .then(response => {
          this.rawSeries = response;
          this.requestStatus = 'success';
          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        })
        .catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
    extractAverageTimeData() {
      if (!this.rawSeries || !this.rawSeries.average_time) {
        return [];
      }

      const entries = Object.entries(this.rawSeries.average_time);

      this.legendLabels.forEach(label => {
        if (!entries.some(([key]) => this.getNameColor(key).name === label)) {
          entries.push([label, { percentage: 0, interval: { days: 0, hours: 0 } }]);
        }
      });

      const dataEntries = entries.map(([key, value]) => ({
        name: this.getNameColor(key).name,
        data: value,
        color: this.getNameColor(key).color,
      }));

      return this.formatDataEntries(dataEntries);
    },

    formatDataEntries(entries) {
      return this.legendLabels.map(label => {
        const entry = entries.find(e => e.name === label);
        return {
          name: label,
          y: entry ? entry.data.percentage : 0,
          color: entry.color,
          interval: entry ? this.formatInterval(entry.data.interval) : this.default_time,
          avg_time_to_capture: this.rawSeries.avg_time_to_capture.percentage,
          interval_avg: this.formatInterval(this.rawSeries.avg_time_to_capture.interval),
        };
      });
    },

    formatInterval(interval) {
      if (!interval || Object.keys(interval).length === 0) {
        return this.default_time;
      }

      const formatDate = (value, label) => {
        return value ? `${value} ${label}${value !== 1 ? 's' : ''}` : '';
      };

      const parts = [
        formatDate(interval.months, this.$t('components.AverageSACTimePieChart.date.month')),
        formatDate(interval.days, this.$t('components.AverageSACTimePieChart.date.day')),
        formatDate(interval.hours, this.$t('components.AverageSACTimePieChart.date.hour')),
        formatDate(interval.minutes, this.$t('components.AverageSACTimePieChart.date.minute')),
        formatDate(interval.seconds, this.$t('components.AverageSACTimePieChart.date.second')),
      ];

      return parts.filter(part => part !== '').join(', ') || this.default_time;
    },

    getNameColor (key) {
      switch (key) {
      case SAC_STATUS.OPEN:
        return {
          name: this.$t('components.AverageSACTimePieChart.label.open'),
          color: colors.statusColor.opened,
        };
      case SAC_STATUS.WAITING:
        return {
          name: this.$t('components.AverageSACTimePieChart.label.waiting'),
          color: colors.statusColor.holding,
        };
      case SAC_STATUS.ANSWER:
        return {
          name: this.$t('components.AverageSACTimePieChart.label.answer'),
          color: colors.statusColor.replied,
        };
      case SAC_STATUS.FINISHED:
        return {
          name: this.$t('components.AverageSACTimePieChart.label.finished'),
          color: colors.statusColor.closed,
        };
      default:
        return {
          name: key,
          color: colors.statusColor.ignored,
        };
      }
    },
  },
};
</script>

